/*
 * SeoPlanBuilder
 * Turns the article's First / Next sequence into a personal list.
 *
 * Rules:
 * - Inputs: firm size (solo / small / multi-office), number of practice areas (1–2 / 3–5 / 6+),
 *   and a checklist of what's already in place.
 * - FIRST list = foundation items NOT ticked, in this fixed order:
 *     tracking -> technical -> gbp -> practicePages
 *   plus, for multi-office firms, "office pages" if not ticked (placed after gbp).
 *   For solo/small firms with 3+ practice areas, a "narrow your focus" item is added first.
 * - NEXT list = compounding items NOT ticked, in this fixed order:
 *     subPractice -> reviews -> citations -> links -> aiTracking
 *   plus, for multi-office firms, "check for competing pages" at the end.
 *   For 6+ practice areas, a "prioritise by case value" item is added first.
 * - If every item applicable to the firm is ticked, the reader is pointed to the free audit.
 * - Results appear once a firm size is chosen. No tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";

type Size = "" | "solo" | "small" | "multi";
type Practices = "few" | "some" | "many";
type Item = { id: string; text: string; href: string; linkText: string };

const FOUNDATION: Item[] = [
  { id: "tracking", text: "Set up Search Console, GA4 key events and call tracking", href: "/services/law-firm-marketing-analytics", linkText: "Analytics & reporting" },
  { id: "technical", text: "Fix indexing, speed and structured-data problems", href: "/services/technical-seo-for-law-firms", linkText: "Technical SEO" },
  { id: "gbp", text: "Complete your Google Business Profile and match your details everywhere", href: "/services/local-seo-for-law-firms", linkText: "Local SEO" },
  { id: "practicePages", text: "Give each practice area its own page, written for clients", href: "/services/legal-content-writing", linkText: "Legal content writing" },
];

const COMPOUNDING: Item[] = [
  { id: "subPractice", text: "Add case-type pages and client-question posts, attorney-reviewed", href: "/services/legal-content-writing", linkText: "Legal content writing" },
  { id: "reviews", text: "Ask every satisfied client for a review and reply to every review", href: "/services/local-seo-for-law-firms", linkText: "Local SEO" },
  { id: "citations", text: "Make your legal directory listings complete and consistent", href: "/services/local-seo-for-law-firms", linkText: "Local SEO" },
  { id: "links", text: "Earn links through bar groups, local press and community work", href: "/services/link-building-for-law-firms", linkText: "Legal link building" },
  { id: "aiTracking", text: "Track visits from AI tools as their own channel in GA4", href: "/services/ai-seo-geo", linkText: "AI SEO & GEO" },
];

const OFFICE: Item = { id: "officePages", text: "One Business Profile and one genuinely local page per real office", href: "/services/local-seo-for-law-firms", linkText: "Local SEO" };

const CHECKS: { id: string; label: string; multiOnly?: boolean }[] = [
  { id: "tracking", label: "Search Console, GA4 key events and call tracking are set up" },
  { id: "technical", label: "A technical audit has been done and the main issues fixed" },
  { id: "gbp", label: "Our Google Business Profile is complete" },
  { id: "officePages", label: "Every office has its own profile and local page", multiOnly: true },
  { id: "practicePages", label: "Each practice area has its own page" },
  { id: "subPractice", label: "We publish case-type pages or client-question posts" },
  { id: "reviews", label: "We ask for and reply to reviews as a habit" },
  { id: "citations", label: "Our legal directory listings are consistent" },
  { id: "links", label: "We earn links from bar groups, press or community work" },
  { id: "aiTracking", label: "We track visits from AI tools in GA4" },
];

export default function SeoPlanBuilder() {
  const uid = useId();
  const [size, setSize] = useState<Size>("");
  const [practices, setPractices] = useState<Practices>("few");
  const [done, setDone] = useState<Record<string, boolean>>({});

  const isMulti = size === "multi";
  const first: Item[] = [];
  if ((size === "solo" || size === "small") && practices !== "few") {
    first.push({ id: "narrow", text: "Pick the one or two practice areas that bring your most valuable cases and build those first", href: "/blog/solo-lawyer-small-firm-seo", linkText: "Solo and small firm SEO" });
  }
  FOUNDATION.forEach((f) => {
    if (!done[f.id]) first.push(f);
    if (f.id === "gbp" && isMulti && !done[OFFICE.id]) first.push(OFFICE);
  });

  const next: Item[] = [];
  if (practices === "many") {
    next.push({ id: "prioritise", text: "Rank your practice-area keywords by intent and case value before adding pages", href: "/services/law-firm-seo", linkText: "Law firm SEO" });
  }
  COMPOUNDING.forEach((c) => {
    if (!done[c.id]) next.push(c);
  });
  if (isMulti) {
    next.push({ id: "cannibal", text: "Check Search Console for two of your pages competing for the same search", href: "/services/technical-seo-for-law-firms", linkText: "Technical SEO" });
  }

  const allDone = first.length === 0 && next.filter((n) => n.id !== "cannibal" && n.id !== "prioritise").length === 0;

  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";
  const pill = (on: boolean) =>
    `h-auto whitespace-normal rounded-md border px-3 py-1.5 text-sm ${focusRing} ${on ? "border-border bg-primary text-primary-foreground" : "border-input bg-card text-card-foreground"}`;

  const renderList = (title: string, items: Item[]) => (
    <div className="mt-3">
      <p className="text-sm font-semibold">{title}</p>
      <ol className="mt-2 space-y-2">
        {items.map((it, i) => (
          <li key={it.id} className="rounded-md border border-border bg-card p-3 text-sm">
            <span className="font-medium">{i + 1}. </span>
            {it.text}{" "}
            <Link to={it.href} className={`font-medium underline underline-offset-4 ${focusRing}`}>
              {it.linkText} →
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">Build your firm's SEO plan</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Answer three questions to get your own first-and-next list. Nothing is saved or sent.
      </p>

      <div className="mt-5">
        <p id={`${uid}-size`} className="text-sm font-medium">1. Your firm</p>
        <div role="group" aria-labelledby={`${uid}-size`} className="mt-2 flex flex-wrap gap-2">
          {([["solo", "Solo practice"], ["small", "Small firm, one office"], ["multi", "Multiple offices"]] as [Size, string][]).map(([v, l]) => (
            <Button variant="outline" key={v} type="button" aria-pressed={size === v} onClick={() => setSize(v)} className={pill(size === v)}>
              {l}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <p id={`${uid}-prac`} className="text-sm font-medium">2. Practice areas you want cases from</p>
        <div role="group" aria-labelledby={`${uid}-prac`} className="mt-2 flex flex-wrap gap-2">
          {([["few", "1–2"], ["some", "3–5"], ["many", "6 or more"]] as [Practices, string][]).map(([v, l]) => (
            <Button variant="outline" key={v} type="button" aria-pressed={practices === v} onClick={() => setPractices(v)} className={pill(practices === v)}>
              {l}
            </Button>
          ))}
        </div>
      </div>

      <fieldset className="mt-5">
        <legend className="text-sm font-medium">3. Already in place (tick all that apply)</legend>
        <ul className="mt-2 grid gap-2 sm:grid-cols-2">
          {CHECKS.filter((c) => !c.multiOnly || isMulti).map((c) => (
            <li key={c.id}>
              <Button variant="outline"
                type="button"
                aria-pressed={!!done[c.id]}
                onClick={() => setDone((p) => ({ ...p, [c.id]: !p[c.id] }))}
                className={`w-full justify-start text-left ${pill(!!done[c.id])}`}
              >
                <span aria-hidden="true">{done[c.id] ? "✓ " : "○ "}</span>
                {c.label}
              </Button>
            </li>
          ))}
        </ul>
      </fieldset>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {!size ? (
          <p className="text-sm text-muted-foreground">Choose your firm type to see your plan.</p>
        ) : allDone ? (
          <p className="text-sm">
            The foundation and the compounding work are both in place. The next gains come from doing them consistently
            and measuring signed cases.{" "}
            <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
              Get a free SEO audit →
            </Link>
          </p>
        ) : (
          <div>
            {first.length > 0 ? renderList("First (months 1–3)", first) : <p className="text-sm font-semibold">Your foundation is in place.</p>}
            {next.length > 0 && renderList(first.length > 0 ? "Next (months 4–9)" : "Next", next)}
            <p className="mt-4 text-sm">
              Want this turned into a 12-month plan for your firm?{" "}
              <Link to="/law-firm-seo-audit" className={`font-medium underline underline-offset-4 ${focusRing}`}>
                Get a free SEO audit →
              </Link>
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
